import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adminApi } from '@/utils/adminApi';
import {
  CalendarDays,
  CheckCircle2,
  CreditCard,
  IndianRupee,
  Plus,
  Search,
  ShieldBan,
  ShieldCheck,
  UserPlus,
  X
} from 'lucide-react';

const initialForm = {
  name: '',
  email: '',
  phone: '',
  password: ''
};

const initialSubscriptionForm = {
  plan: 'Gold',
  startDate: '',
  endDate: '',
  amountPaid: ''
};

export default function AdminOwnersPage() {
  const [adminProfile, setAdminProfile] = useState(null);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState(initialForm);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState(null);
  const [subscriptionForm, setSubscriptionForm] = useState(initialSubscriptionForm);

  const fetchOwners = async () => {
    try {
      const [ownersResponse, meResponse] = await Promise.all([
        adminApi.getOwners(),
        adminApi.getMe()
      ]);
      setOwners(ownersResponse.data);
      setAdminProfile(meResponse.data);
      localStorage.setItem('admin_profile', JSON.stringify(meResponse.data));
    } catch (error) {
      toast.error(error.message || 'Failed to load owners');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOwners();
  }, []);

  const filteredOwners = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return owners;
    }

    return owners.filter((owner) =>
      [owner.name, owner.email, owner.phone]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term))
    );
  }, [owners, searchTerm]);

  const activeOwners = owners.filter((owner) => owner.status?.toLowerCase() === 'active').length;
  const blockedOwners = owners.length - activeOwners;
  const isSuperAdmin = adminProfile?.role === 'SUPER_ADMIN';

  const formatDate = (value) => value ? new Date(value).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }) : 'Not set';

  const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

  const handleCreateOwner = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await adminApi.createOwner(formData);
      toast.success('Owner account created successfully');
      setFormData(initialForm);
      setShowCreateModal(false);
      fetchOwners();
    } catch (error) {
      toast.error(error.message || 'Failed to create owner');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (userId) => {
    try {
      await adminApi.toggleOwnerStatus(userId);
      toast.success('Owner status updated');
      fetchOwners();
    } catch (error) {
      toast.error(error.message || 'Failed to update owner status');
    }
  };

  const openSubscriptionModal = (owner) => {
    setSelectedOwner(owner);
    setSubscriptionForm({
      plan: owner.subscription?.plan || 'Gold',
      startDate: owner.subscription?.startDate || '',
      endDate: owner.subscription?.endDate || '',
      amountPaid: owner.subscription?.amountPaid ?? ''
    });
    setShowSubscriptionModal(true);
  };

  const handleSubscriptionSubmit = async (e) => {
    e.preventDefault();
    if (!selectedOwner) {
      return;
    }

    setSubmitting(true);
    try {
      await adminApi.upsertOwnerSubscription(selectedOwner.id, {
        ...subscriptionForm,
        amountPaid: Number(subscriptionForm.amountPaid)
      });
      toast.success('Subscription updated successfully');
      setShowSubscriptionModal(false);
      setSelectedOwner(null);
      setSubscriptionForm(initialSubscriptionForm);
      fetchOwners();
    } catch (error) {
      toast.error(error.message || 'Failed to update subscription');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!selectedOwner?.subscription) {
      toast.error('No subscription found for this owner');
      return;
    }

    setSubmitting(true);
    try {
      await adminApi.cancelOwnerSubscription(selectedOwner.id);
      toast.success('Subscription cancelled successfully');
      setShowSubscriptionModal(false);
      setSelectedOwner(null);
      setSubscriptionForm(initialSubscriptionForm);
      fetchOwners();
    } catch (error) {
      toast.error(error.message || 'Failed to cancel subscription');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6 lg:space-y-8">
        <section className="rounded-[34px] border border-slate-200 bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,0.06)] sm:p-8">
          <div className="grid gap-5 xl:grid-cols-[minmax(0,1.5fr)_auto] xl:items-end">
            <div className="min-w-0">
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-emerald-700">Owners</p>
              <h2 className="mt-2 text-3xl font-bold tracking-tight text-slate-950 sm:text-4xl">
                Create accounts on behalf of owners and control access instantly.
              </h2>
              <p className="mt-4 text-base leading-7 text-slate-600 sm:text-lg">
                This workspace is your operating layer for onboarding new owners, blocking or reactivating accounts, and reviewing how much inventory each owner manages.
              </p>
            </div>

            {isSuperAdmin && (
              <Button
                onClick={() => setShowCreateModal(true)}
                className="h-14 w-full rounded-full bg-slate-950 px-6 text-base font-semibold text-white hover:bg-slate-800 sm:w-auto xl:justify-self-end"
              >
                <Plus className="mr-2 h-4 w-4" />
                Create owner account
              </Button>
            )}
          </div>
        </section>

        <section className="grid gap-5 md:grid-cols-3">
          <div className="rounded-[30px] bg-slate-950 p-6 text-white shadow-[0_20px_45px_rgba(15,23,42,0.1)]">
            <p className="text-sm font-semibold text-slate-300">Total owners</p>
            <p className="mt-3 text-4xl font-bold">{owners.length}</p>
          </div>
          <div className="rounded-[30px] bg-emerald-500 p-6 text-white shadow-[0_20px_45px_rgba(16,185,129,0.15)]">
            <p className="text-sm font-semibold text-emerald-50">Active owners</p>
            <p className="mt-3 text-4xl font-bold">{activeOwners}</p>
          </div>
          <div className="rounded-[30px] bg-rose-500 p-6 text-white shadow-[0_20px_45px_rgba(244,63,94,0.15)]">
            <p className="text-sm font-semibold text-rose-50">Blocked owners</p>
            <p className="mt-3 text-4xl font-bold">{blockedOwners}</p>
          </div>
        </section>

        <section className="min-w-0 rounded-[34px] border border-slate-200 bg-white p-6 shadow-[0_24px_70px_rgba(15,23,42,0.06)]">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <h3 className="text-2xl font-bold text-slate-950">Owner directory</h3>
              <p className="mt-1 text-slate-600">Search, review, and change owner access from one table.</p>
            </div>

            <div className="relative w-full max-w-md">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email, or phone"
                className="h-12 rounded-2xl border-slate-200 pl-11"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-14">
              <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-slate-950" />
            </div>
          ) : (
            <div className="mt-6 overflow-hidden rounded-[26px] border border-slate-200">
              <div className="overflow-x-auto">
                <table className="min-w-[1280px] w-full text-left">
                  <thead className="bg-slate-50">
                    <tr className="text-sm text-slate-500">
                      <th className="px-4 py-4 font-semibold">Owner</th>
                      <th className="px-4 py-4 font-semibold">Contact</th>
                      <th className="px-4 py-4 font-semibold">Portfolio</th>
                      <th className="px-4 py-4 font-semibold">Occupancy</th>
                      <th className="px-4 py-4 font-semibold">Subscription</th>
                      <th className="px-4 py-4 font-semibold">Status</th>
                      <th className="px-4 py-4 font-semibold text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredOwners.map((owner) => {
                      const isActive = owner.status?.toLowerCase() === 'active';
                      return (
                        <tr key={owner.id} className="border-t border-slate-200 bg-white align-top">
                          <td className="px-4 py-4">
                            <p className="font-semibold text-slate-950">{owner.name}</p>
                            <p className="text-sm text-slate-500">{owner.email}</p>
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600">
                            <p>{owner.phone || 'No phone added'}</p>
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600">
                            <p>{owner.totalPgs} PGs</p>
                            <p>{owner.totalBeds} beds</p>
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600">
                            <p>{owner.activeTenants} active tenants</p>
                            <p>{owner.occupiedBeds} occupied beds</p>
                          </td>
                          <td className="px-4 py-4 text-sm text-slate-600">
                            {owner.subscription ? (
                              <div className="space-y-1">
                                <p className="font-semibold text-slate-900">{owner.subscription.plan}</p>
                                <p>{formatDate(owner.subscription.startDate)} to {formatDate(owner.subscription.endDate)}</p>
                                <p>{formatCurrency(owner.subscription.amountPaid)}</p>
                                <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                                  owner.subscription.status === 'ACTIVE'
                                    ? 'bg-emerald-50 text-emerald-700'
                                    : owner.subscription.status === 'CANCELLED'
                                      ? 'bg-amber-50 text-amber-700'
                                      : 'bg-rose-50 text-rose-700'
                                }`}>
                                  {owner.subscription.status}
                                </span>
                              </div>
                            ) : (
                              <div className="space-y-1">
                                <p className="font-semibold text-amber-700">No subscription</p>
                                <p className="max-w-[220px] text-xs text-amber-700">Please contact support you don't have any subscription.</p>
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                              isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                            }`}>
                              {isActive ? 'Active' : 'Blocked'}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                className="rounded-full border border-slate-200 px-4 text-slate-700 hover:bg-slate-50"
                                onClick={() => openSubscriptionModal(owner)}
                              >
                                <CreditCard className="mr-2 h-4 w-4" />
                                Manage subscription
                              </Button>
                              <Button
                                variant="outline"
                                className={`rounded-full border px-4 ${
                                  isActive
                                    ? 'border-rose-200 text-rose-700 hover:bg-rose-50'
                                    : 'border-emerald-200 text-emerald-700 hover:bg-emerald-50'
                                }`}
                                onClick={() => handleToggleStatus(owner.id)}
                              >
                                {isActive ? (
                                  <>
                                    <ShieldBan className="mr-2 h-4 w-4" />
                                    Block
                                  </>
                                ) : (
                                  <>
                                    <ShieldCheck className="mr-2 h-4 w-4" />
                                    Activate
                                  </>
                                )}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {filteredOwners.length === 0 && (
                <div className="bg-slate-50 px-4 py-10 text-center text-slate-500">
                  No owners matched your search.
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-[34px] border border-slate-200 bg-white p-6 shadow-[0_30px_90px_rgba(15,23,42,0.22)] sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-700">Create Owner</p>
                <h3 className="mt-2 text-2xl font-bold text-slate-950">Open an account for a new PG owner</h3>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  This creates the owner account directly from super admin and makes it available for login immediately.
                </p>
              </div>

              <button
                onClick={() => setShowCreateModal(false)}
                className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleCreateOwner} className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="mb-2 block text-sm font-semibold text-slate-700">Full name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter full name"
                  className="h-12 rounded-2xl border-slate-200"
                  required
                />
              </div>
              <div>
                <Label className="mb-2 block text-sm font-semibold text-slate-700">Phone number</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  placeholder="10-digit mobile number"
                  className="h-12 rounded-2xl border-slate-200"
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="mb-2 block text-sm font-semibold text-slate-700">Email address</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="owner@example.com"
                  className="h-12 rounded-2xl border-slate-200"
                  required
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="mb-2 block text-sm font-semibold text-slate-700">Temporary password</Label>
                <Input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Set a password for the owner"
                  className="h-12 rounded-2xl border-slate-200"
                  required
                />
              </div>

              <div className="sm:col-span-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                <div className="flex items-center gap-2 font-semibold">
                  <CheckCircle2 className="h-4 w-4" />
                  The owner can log in immediately after creation.
                </div>
              </div>

              <div className="sm:col-span-2 flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 rounded-full border-slate-300"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="h-12 rounded-full bg-slate-950 px-6 text-white hover:bg-slate-800"
                  disabled={submitting}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  {submitting ? 'Creating...' : 'Create owner'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showSubscriptionModal && selectedOwner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm">
          <div className="w-full max-w-2xl rounded-[34px] border border-slate-200 bg-white p-6 shadow-[0_30px_90px_rgba(15,23,42,0.22)] sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-emerald-700">Subscription Control</p>
                <h3 className="mt-2 text-2xl font-bold text-slate-950">{selectedOwner.name}</h3>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  Assign, update, or cancel this owner&apos;s subscription from one place.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowSubscriptionModal(false);
                  setSelectedOwner(null);
                  setSubscriptionForm(initialSubscriptionForm);
                }}
                className="rounded-full border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              {selectedOwner.subscription ? (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Current plan</p>
                    <p className="mt-1 text-lg font-bold text-slate-950">{selectedOwner.subscription.plan}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Current status</p>
                    <p className="mt-1 text-lg font-bold text-slate-950">{selectedOwner.subscription.status}</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm font-medium text-amber-700">No subscription found. Assign one now to activate billing visibility.</p>
              )}
            </div>

            <form onSubmit={handleSubscriptionSubmit} className="mt-6 grid gap-4 sm:grid-cols-2">
              <div>
                <Label className="mb-2 block text-sm font-semibold text-slate-700">Plan</Label>
                <select
                  value={subscriptionForm.plan}
                  onChange={(e) => setSubscriptionForm({ ...subscriptionForm, plan: e.target.value })}
                  className="h-12 w-full rounded-2xl border border-slate-200 bg-white px-4"
                  required
                >
                  <option value="Standard">Standard</option>
                  <option value="Gold">Gold</option>
                  <option value="Platinum">Platinum</option>
                </select>
              </div>
              <div>
                <Label className="mb-2 block text-sm font-semibold text-slate-700">Amount paid</Label>
                <div className="relative">
                  <IndianRupee className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={subscriptionForm.amountPaid}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, amountPaid: e.target.value })}
                    className="h-12 rounded-2xl border-slate-200 pl-10"
                    placeholder="Enter amount paid"
                    required
                  />
                </div>
              </div>
              <div>
                <Label className="mb-2 block text-sm font-semibold text-slate-700">Start date</Label>
                <div className="relative">
                  <CalendarDays className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="date"
                    value={subscriptionForm.startDate}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, startDate: e.target.value })}
                    className="h-12 rounded-2xl border-slate-200 pl-10"
                    required
                  />
                </div>
              </div>
              <div>
                <Label className="mb-2 block text-sm font-semibold text-slate-700">End date</Label>
                <div className="relative">
                  <CalendarDays className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="date"
                    value={subscriptionForm.endDate}
                    onChange={(e) => setSubscriptionForm({ ...subscriptionForm, endDate: e.target.value })}
                    className="h-12 rounded-2xl border-slate-200 pl-10"
                    required
                  />
                </div>
              </div>

              <div className="sm:col-span-2 flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-between">
                <Button
                  type="button"
                  variant="outline"
                  className="h-12 rounded-full border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={handleCancelSubscription}
                  disabled={submitting || !selectedOwner.subscription}
                >
                  Cancel subscription
                </Button>
                <div className="flex flex-col-reverse gap-3 sm:flex-row">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 rounded-full border-slate-300"
                    onClick={() => {
                      setShowSubscriptionModal(false);
                      setSelectedOwner(null);
                      setSubscriptionForm(initialSubscriptionForm);
                    }}
                  >
                    Close
                  </Button>
                  <Button
                    type="submit"
                    className="h-12 rounded-full bg-slate-950 px-6 text-white hover:bg-slate-800"
                    disabled={submitting}
                  >
                    {submitting ? 'Saving...' : 'Save subscription'}
                  </Button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
